import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDevice, useSensorData, useFieldPending, useMTCommand } from '@/hooks';
import { deviceService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { Button } from '@/components/common/Button/Button';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { PendingBadge, ToggleSwitch } from '@/components/common/ConfigControls';
import { NevataStatusDisplay } from '@/components/common/NevataStatusDisplay';
import { findPendingMT, getCombinedAddress } from '@/lib/sensor-commands';
import { formatDateTime } from '@/lib/formatters';
import type { NASecurityObject } from '@/api/models';
import {
  isWaterZone, getZoneDisplayName, getZoneSensorLabel, resolveOnlineState, onlineStateVariant,
  resolveAlertState, alertStateVariant,
} from '@/lib/enum-resolvers';
import { isIntegratedXPulseSensor } from '@/lib/product-capabilities';

export function FloatSwitchPanel() {
  const { deviceUUID } = useParams<{ deviceUUID: string }>();
  const { selectedDeviceInfo, deviceInfoLoading, refreshDeviceInfo } = useDevice();
  const { sensorData } = useSensorData();
  const [refreshing, setRefreshing] = useState(false);
  const [editingZoneNum, setEditingZoneNum] = useState<number | null>(null);
  const [editingDescriptor, setEditingDescriptor] = useState('');
  const [descriptorSaving, setDescriptorSaving] = useState(false);
  const [connectedFeedback, setConnectedFeedback] = useState<string | null>(null);
  const fieldPending = useFieldPending();
  const { sendCommand, mtObjList } = useMTCommand(deviceUUID ?? '');

  // Mobile's sensorConnectedCallback (SensorsSettingsPageViewModel.cs, "Float
  // Switch Connected" label) — a manual, persisted override ("is a real
  // sensor actually wired to this zone slot"), not device-reported telemetry.
  // REST write, not an MT command — see the identical control on
  // SecurityZoneListPanel.tsx for the full rationale.
  const handleToggleConnected = useCallback(
    (zone: NASecurityObject) => {
      if (!deviceUUID) return;
      setConnectedFeedback(null);
      return fieldPending.run(`zone-connected-${zone.zoneNum}`, !zone.isConnected, async () => {
        try {
          await deviceService.setDeviceProperties({
            deviceUUID,
            containedObj: {
              sourceAddressMSB: zone.sourceAddressMSB,
              sourceAddressLSB: zone.sourceAddressLSB,
              zoneInstance: zone.zoneInstance,
              isConnected: !zone.isConnected,
            },
          });
        } catch {
          setConnectedFeedback('Failed to update sensor connected state.');
          return;
        }
        await refreshDeviceInfo();
      });
    },
    [deviceUUID, refreshDeviceInfo, fieldPending],
  );

  // Backend's formatMTCommandDescriptorCmd (NAMTObject.cs) requires a 3-element
  // dataStrList: [combinedAddress, zoneNum, descriptor] — matches mobile's
  // SensorsSettingsPageViewModel.sensorDescriptorNameChangedCallback exactly.
  const handleSaveZoneDescriptor = async (zone: { zoneNum: number; sourceAddressMSB: number; sourceAddressLSB: number }) => {
    if (!deviceUUID) return;
    setDescriptorSaving(true);
    const newDescriptor = editingDescriptor;
    try {
      await fieldPending.run(`zone-descriptor-${zone.zoneNum}`, newDescriptor, () =>
        sendCommand(NAMTMessageType.MT_MESSAGE_SET_ZONE_DESCRIPTORS, [
          getCombinedAddress(zone.sourceAddressMSB, zone.sourceAddressLSB),
          String(zone.zoneNum),
          newDescriptor,
        ]),
      );
    } finally {
      setDescriptorSaving(false);
      setEditingZoneNum(null);
    }
  };

  // Keep the button spinning until the device actually receives the command
  // (or the pending window times out), not just until the enqueue POST
  // returns — the REST call resolves as soon as it's queued, well before the
  // device checks in.
  const refreshPending = refreshing || !!findPendingMT(mtObjList, NAMTMessageType.GET_SENSOR_DATA_REQUEST);

  const handleRefresh = async () => {
    if (!deviceUUID) return;
    setRefreshing(true);
    try {
      await sendCommand(NAMTMessageType.GET_SENSOR_DATA_REQUEST);
    } finally {
      setRefreshing(false);
    }
  };

  const nevataSensors = sensorData?.nevataSensorList ?? [];
  const waterZones = (sensorData?.securitySensorlist ?? []).filter((z) => isWaterZone(z.alarmType));

  if (deviceInfoLoading && !selectedDeviceInfo) {
    return <PageSpinner />;
  }

  if (!selectedDeviceInfo) {
    return (
      <EmptyState
        title="No Device Selected"
        description="Select a device to view bilge and float switch data."
      />
    );
  }

  const hasNevataData = nevataSensors.length > 0;
  const hasWaterZones = waterZones.length > 0;

  if (!hasNevataData && !hasWaterZones) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Bilge & Float Switch</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor bilge levels and pump activity
          </p>
        </div>
        <EmptyState
          title="No Float Switch Data"
          description="No float switch or bilge sensors are configured on this device."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Bilge & Float Switch</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Monitor bilge levels and pump activity
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={handleRefresh} loading={refreshPending}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          On-Demand
        </Button>
      </div>

      {connectedFeedback && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <div className="flex items-center justify-between">
            <span>{connectedFeedback}</span>
            <button type="button" onClick={() => setConnectedFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Section: Water Zones (from security sensor list) */}
      {hasWaterZones && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">Water Zones</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {waterZones.map((zone, idx) => {
            // Pending descriptor commands carry the new value in their own
            // payload (dataStrList[2]) — fall through to it once the local
            // optimistic value clears (right after the enqueue POST resolves)
            // so the field keeps showing the new value for the whole time
            // the command is actually pending on the device, not just during
            // the POST.
            const pendingDescriptorMT = findPendingMT(
              mtObjList,
              NAMTMessageType.MT_MESSAGE_SET_ZONE_DESCRIPTORS,
              (d) => d[1] === String(zone.zoneNum),
            );
            const zoneDescriptor =
              fieldPending.getOptimisticValue<string>(`zone-descriptor-${zone.zoneNum}`) ??
              pendingDescriptorMT?.dataStrList?.[2] ??
              zone.descriptor;
            const zoneDescriptorPending = fieldPending.isPending(`zone-descriptor-${zone.zoneNum}`) || !!pendingDescriptorMT;
            const connectedKey = `zone-connected-${zone.zoneNum}`;
            const zoneConnected = fieldPending.getOptimisticValue<boolean>(connectedKey) ?? zone.isConnected;
            const zoneConnectedPending = fieldPending.isPending(connectedKey);
            return (
              <div
                key={`water-zone-${zone.zoneNum}-${idx}`}
                className={`rounded-xl border p-5 ${
                  zone.alarmPresent
                    ? 'border-red-400 bg-red-50 dark:border-red-600 dark:bg-red-900/10'
                    : 'border-slate-200 bg-white dark:border-navy-700 dark:bg-navy-900'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-4 w-4 rounded-full ${
                        zone.alarmPresent
                          ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'
                          : 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                      }`}
                    />
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white">
                        {getZoneDisplayName(zone)}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {/* Mobile's "{sensorName}, Zone {N}" (updateFloatSwitchCategory,
                            DeviceCategoryMetrics.cs) — zoneInstance is per-physical-sensor,
                            0-based, so +1 identifies which of that sensor's own zone slots
                            this is, unlike zoneNum's device-wide enumeration. */}
                        {getZoneSensorLabel(zone)}, Zone {zone.zoneInstance + 1}
                      </p>
                    </div>
                  </div>
                  {isIntegratedXPulseSensor(zone) ? (
                    <StatusBadge label="Built-in" variant="info" dot />
                  ) : (
                    <StatusBadge
                      label={resolveOnlineState(zone.onlineState)}
                      variant={onlineStateVariant(zone.onlineState)}
                      dot
                    />
                  )}
                </div>

                {/* Descriptor */}
                <div className="mt-2 flex items-center text-xs">
                  {editingZoneNum === zone.zoneNum ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        autoFocus
                        maxLength={15}
                        value={editingDescriptor}
                        onChange={(e) => setEditingDescriptor(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveZoneDescriptor(zone);
                          if (e.key === 'Escape') setEditingZoneNum(null);
                        }}
                        className="w-32 rounded border border-teal-400 bg-transparent px-1.5 py-0.5 text-xs text-slate-800 dark:text-slate-100 focus:outline-none"
                      />
                      <button type="button" disabled={descriptorSaving} onClick={() => handleSaveZoneDescriptor(zone)} className="text-teal-500 hover:text-teal-400 disabled:opacity-50">
                        {descriptorSaving ? '…' : '✓'}
                      </button>
                      <button type="button" onClick={() => setEditingZoneNum(null)} className="text-slate-400 hover:text-slate-300">✕</button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setEditingZoneNum(zone.zoneNum); setEditingDescriptor(zoneDescriptor ?? ''); }}
                      className="flex items-center gap-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    >
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        {zoneDescriptor || <em className="text-slate-400">No description</em>}
                      </span>
                      <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                      </svg>
                    </button>
                  )}
                  {zoneDescriptorPending && <PendingBadge />}
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                  {zone.alarmPresent && (
                    <StatusBadge label="ALARM" variant="danger" dot />
                  )}
                  {/* Connected/Disconnected is now shown by the "Float Switch
                      Connected" toggle below — a static badge here would just
                      repeat it. */}
                </div>
                {/* Mobile's "Float Switch Connected" toggle (SensorsSettingsPageViewModel.cs)
                    — same manual persisted override as Intrusion Zones' "Sensor Connected". */}
                <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 dark:border-navy-700">
                  <span className="flex items-center text-xs text-slate-500 dark:text-slate-400">
                    Float Switch Connected
                    {zoneConnectedPending && <PendingBadge />}
                  </span>
                  <ToggleSwitch
                    enabled={!!zoneConnected}
                    loading={false}
                    onToggle={() => handleToggleConnected(zone)}
                  />
                </div>
                {zone.updatedDT && (
                  <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
                    Updated: {formatDateTime(zone.updatedDT)}
                  </p>
                )}
              </div>
            );})}
          </div>
        </div>
      )}


      {/* Section: Nevata Sensors */}
      {hasNevataData && (
        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">Bilge Sensors (Nevata)</h2>
          <div className="grid gap-4 lg:grid-cols-2">
            {nevataSensors.map((nev, idx) => (
              <div
                key={`nevata-${idx}`}
                className="rounded-xl border border-slate-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-900"
              >
                {/* Header */}
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white">
                      {nev.sensorName || `Nevata Sensor ${idx + 1}`}
                    </h3>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusBadge
                        label={resolveOnlineState(nev.onlineState)}
                        variant={onlineStateVariant(nev.onlineState)}
                        dot
                      />
                      <StatusBadge
                        label={resolveAlertState(nev.alertState)}
                        variant={alertStateVariant(nev.alertState)}
                        dot
                      />
                      {nev.isCalibrated ? (
                        <StatusBadge label="Calibrated" variant="success" />
                      ) : (
                        <StatusBadge label="Not Calibrated" variant="warning" />
                      )}
                    </div>
                  </div>
                </div>

                <NevataStatusDisplay
                  nev={nev}
                  inNoDischargeZone={selectedDeviceInfo.globalSettings?.inNoDischargeZone}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
